/**
 * InteractiveMapHero — Weidner Via6 page hero for /interactive-map.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Interactive map hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function InteractiveMapHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="interactivemaphero.title"
        bgPath="interactivemaphero.bg_image"
        bgDef={bgDef}
        defaultTitle="Interactive Map"
        keywords={['map', 'exterior', 'building', 'aerial', 'site']}
        pageHint="interactivemaphero"
        altText="Interactive map hero image"
      />

      <RichTextContent
        path="interactivemaphero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="interactivemaphero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapHero',
  displayName: 'Interactive Map Hero',
  tokens: {
    'interactivemaphero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Interactive Map',
    },
  },
  slotGroups: {
    'interactivemaphero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Interactive map hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(InteractiveMapHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapHero;
